/**
 * The Recruiting screen's pure half: which list an applicant belongs in, and the words for what
 * happened. Pure so it can be tested without a browser (recruitingView.test.mjs).
 *
 * ⛔ EVERY LABEL SAYS WHAT IS KNOWN, NOT WHAT WOULD BE NICE. A transfer the platform could not
 * confirm reads "Tried to put them through", never "Talked to the recruiter"; a hand-off that was
 * only logged reads "Email not sent", never "Recruiter emailed". The board is where a recruiter
 * decides whether to pick up the phone, so a flattering label costs a driver.
 */

export const TABS = [
  { v: 'missed', label: 'Missed' },
  { v: 'new', label: 'New' },
  { v: 'screened', label: 'Talked' },
  { v: 'hired', label: 'Hired' },
  { v: 'all', label: 'All' }
];

/** Which tabs an applicant shows under ('all' always). */
export function tabsFor(a) {
  const out = ['all'];
  if (a.missed) out.push('missed');
  if (a.status === 'new' && a.source !== 'import') out.push('new');
  if (['screening', 'screened', 'handed_off', 'forward'].includes(a.status)) out.push('screened');
  if (a.status === 'hired') out.push('hired');
  return out;
}

export const inTab = (list, tab) => (list || []).filter((a) => tabsFor(a).includes(tab));

export const MATCH = {
  match: { label: 'Looks like a fit', fill: true },
  check: { label: 'Needs a question or two' },
  no: { label: 'Not what you hire for', muted: true }
};

export const STATUS = {
  // "Screened" claimed more than a finished call shows (a wrong number is a finished call too).
  new: 'New', screening: 'On the line', screened: 'Talked with Alfred', handed_off: 'With the recruiter', forward: 'Moving forward',
  hired: 'Hired', not_now: 'Not now', do_not_contact: 'Do not contact'
};

export function experienceLabel(months) {
  if (months == null) return null;
  if (months < 12) return `${months} month${months === 1 ? '' : 's'}`;
  const y = Math.floor(months / 12), r = months % 12;
  return r ? `${y} yr ${r} mo` : `${y} year${y === 1 ? '' : 's'}`;
}

const CDL = { A: 'Class A', B: 'Class B', C: 'Class C', CLP: 'Permit', none: 'No CDL' };
const ENDORSE = { H: 'Hazmat', N: 'Tanker', T: 'Doubles', X: 'Tanker+Hazmat', P: 'Passenger', S: 'School bus' };

/** One line about the applicant: "Class A · Tanker · 5 years · Katy, TX". Only what is known. */
export function summaryLine(a) {
  return [
    CDL[a.cdlClass],
    (a.endorsements || []).map((e) => ENDORSE[e]).filter(Boolean).join('+') || null,
    experienceLabel(a.experienceMonths),
    [a.city, a.state].filter(Boolean).join(', ') || null
  ].filter(Boolean).join(' · ');
}

/** What the last call came to, in the recruiter's terms. */
export function callLabel(call) {
  if (!call) return null;
  if (call.status === 'queued' || call.status === 'placed') return 'Alfred is calling…';
  if (call.status === 'failed') return 'Call did not go through';
  if (call.status === 'no_answer') return call.outcome === 'voicemail' ? 'Voicemail — no message left' : 'No answer';
  // "Screened" claimed more than a finished call shows: "wrong number" is a finished call too (review).
  return 'Talked with Alfred';
}

export function transferLabel(transfer) {
  if (!transfer || !transfer.attempted) return null;
  return {
    unconfirmed: 'Tried to put them through (not confirmed)',
    no_answer: 'Tried to put them through — recruiter did not answer',
    declined: 'Tried to put them through — recruiter could not take it',
    declined_with_message: 'Recruiter could not take it and left a message'
  }[transfer.outcome] || 'Tried to put them through';
}

export function handoffLabel(call) {
  if (!call || !call.handoff) return null;
  if (call.handoff === 'emailed') return 'Recruiter emailed';
  if (call.handoff === 'skipped') return null;
  return 'Email not sent';
}

/** A file → the text the import route takes. Browsers hand us a File; tests hand us a string. */
export async function fileText(f) {
  if (typeof f === 'string') return f;
  return f && typeof f.text === 'function' ? f.text() : '';
}
